
import React, { useEffect } from 'react';
import { type EnhancedCrawlResult } from '@/types/crawler';
import { CheckCircle, XCircle } from 'lucide-react';
import BasicStats from './BasicStats';
import SitemapRatingWidget from '@/components/SitemapRatingWidget';

interface OverviewPanelProps {
  crawlResult: EnhancedCrawlResult;
  onSelectPage?: (url: string) => void;
}

const OverviewPanel: React.FC<OverviewPanelProps> = ({ 
  crawlResult,
  onSelectPage 
}) => {
  // Determine if we should show the sitemap section
  const showSitemapWidget = crawlResult.sitemap;
  
  // Use effect to remove any "top performing pages" sections that might be added dynamically
  useEffect(() => {
    const checkAndRemoveTopPerformingPages = () => {
      // Skip AI Readiness removal - it's now a legitimate feature
      
      // Look for top performing pages sections using various selectors that might match
      const selectors = [
        '[data-testid="top-performing-pages"]',
        '[data-testid="top-pages"]',
        '.top-performing-pages', 
        '.top-pages',
        'div:has(h3:contains("Top Performing Pages"))',
        'div:has(h3:contains("Top Pages"))',
        'section:has(h3:contains("Top Performing Pages"))',
        'section:has(h3:contains("Top Pages"))'
      ];
      
      selectors.forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach(el => {
            console.log('Found and removing Top Performing Pages section:', el);
            el.remove();
          });
        } catch (e) {
          // Some selectors might not be valid in all browsers, ignore errors
        }
      });
      
      // Also look for headings that might indicate these sections
      const headingsToRemove = ['Top Performing Pages', 'Top Pages'];
      const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
      
      headings.forEach(heading => {
        const text = heading.textContent?.toLowerCase() || '';
        
        // Check if heading contains any of our target phrases
        const shouldRemove = headingsToRemove.some(phrase => 
          text.toLowerCase().includes(phrase.toLowerCase())
        );
        
        if (shouldRemove) {
          console.log('Found heading with restricted content:', heading);
          const section = heading.closest('section') || 
                         heading.closest('div') || 
                         heading.parentElement;
                         
          if (section) section.remove();
          else heading.remove();
        }
      });
    };
    
    // Run immediately and also after a short delay to catch dynamically added elements
    checkAndRemoveTopPerformingPages();
    const timeoutId = setTimeout(checkAndRemoveTopPerformingPages, 500);
    
    // Run repeatedly to catch any dynamically added elements
    const intervalId = setInterval(checkAndRemoveTopPerformingPages, 1000);
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, []);
  
  return (
    <div className="glass-panel rounded-xl p-6 bg-black">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold">Overview</h2>
          <p className="text-sm text-muted-foreground">
            Crawled URL: <span className="font-medium">{crawlResult.url}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <span className="text-sm px-3 py-1 rounded-full glass-panel bg-black/80 border border-gray-700">
            {crawlResult.pages.length} Pages Crawled
          </span>
        </div>
      </div>
      
      <BasicStats crawlResult={crawlResult} />
      
      {/* Sitemap Section - Only show if there's a sitemap */}
      {showSitemapWidget && (
        <SitemapRatingWidget 
          result={crawlResult} 
          onSelectPage={onSelectPage} 
        />
      )}
    </div>
  );
};

export default OverviewPanel;
